import { Checkbox, Form, Input, Select } from "antd";
import React, { useEffect } from "react";
import {
  getArticleAttrCells,
  hasAttrArticleCounter,
  validateArticle,
} from "src/common/attrEdit/components";
import { ZAttribute } from "src/types/ZAttribute";
import { observer } from "mobx-react-lite";
import { InputNumberStd } from "src/components/InputNumberStd";
import { ZUid } from "src/types/zUid";
import styles from "./ArticleTemplateInput.module.less";
import { attsName, gbName, makeFormName, maskLinePartName } from "./names";

interface Store {
  attsDict: Record<ZUid, ZAttribute>;
  attrTypesMap: Record<number, string>;
}

type PropsArticleTemplateInput = {
  store: Store;
  objectId: ZUid;
  baseName: string;
  disabled?: boolean;
};

const alphanumericCounterName = (baseName: string) =>
  makeFormName(baseName, "alphanumericCounter");

export const ArticleTemplateInput: React.FC<PropsArticleTemplateInput> =
  observer(({ store, baseName, disabled }) => {
    const attsOptions = Object.values(store.attsDict)
      .map((v) => ({
        label: v.name,
        value: v.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const maskLineName = maskLinePartName(baseName);
    const form = Form.useFormInstance();
    const safeVal = String(Form.useWatch(maskLineName) || "");
    const hasCounter = hasAttrArticleCounter(safeVal);
    const cells = getArticleAttrCells(safeVal);
    const uniqCells = Array.from(
      new Map(cells.map((c) => [c.toLocaleUpperCase(), c])).values(),
    );

    useEffect(() => {
      uniqCells.forEach((v, i) => {
        const path = attsName(baseName, i, "linePart");
        const current = form.getFieldValue(path);
        if (current !== v) {
          form.setFieldValue(path, v);
        }
      });
    }, [uniqCells]);

    return (
      <div className={styles.box}>
        <Form.Item
          name={maskLineName}
          label="Шаблон артикула"
          rules={[{ required: true }, validateArticle()]}
        >
          <Input disabled={disabled} />
        </Form.Item>

        {uniqCells.map((c, i) => (
          <div key={c} className={styles.row}>
            <Form.Item
              label="Составляющая"
              name={attsName(baseName, i, "linePart")}
            >
              <Input disabled />
            </Form.Item>
            <Form.Item
              label="Атрибут"
              name={attsName(baseName, i, "attributeId")}
              rules={[{ required: true }]}
            >
              <Select
                options={attsOptions}
                showSearch
                optionFilterProp="label"
                allowClear
                disabled={disabled}
              />
            </Form.Item>
            <Form.Item
              label="Количество символов"
              name={attsName(baseName, i, "quantitySymbols")}
              rules={[{ required: true }]}
            >
              <InputNumberStd
                style={{ width: "100%" }}
                min={0}
                disabled={disabled}
              />
            </Form.Item>
            <Form.Item
              label="Cтрока замены"
              name={attsName(baseName, i, "replaceLine")}
              rules={[{ required: true }]}
            >
              <Input disabled={disabled} />
            </Form.Item>
          </div>
        ))}

        {hasCounter && (
          <div className={styles.row}>
            <Form.Item
              label="Группировать счетчик"
              name={gbName(baseName, "attrIds")}
            >
              <Select
                mode="multiple"
                maxTagCount="responsive"
                options={attsOptions}
                disabled={disabled}
              />
            </Form.Item>
            <Form.Item
              label="Количество символов"
              name={gbName(baseName, "quantitySymbols")}
              dependencies={[alphanumericCounterName(baseName)]}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const alphanumeric = getFieldValue(
                      alphanumericCounterName(baseName),
                    );
                    if (alphanumeric && value < 2) {
                      return Promise.reject(
                        Error("Минимум 2 символа: 1 буква + числовая часть"),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumberStd
                style={{ width: "100%" }}
                min={0}
                disabled={disabled}
              />
            </Form.Item>
            <Form.Item
              label="Начало отсчета"
              name={gbName(baseName, "counterStart")}
              rules={[{ required: true }]}
            >
              <InputNumberStd
                style={{ width: "100%" }}
                min={0}
                disabled={disabled}
              />
            </Form.Item>
            <Form.Item
              className={styles.checkboxCell}
              name={alphanumericCounterName(baseName)}
              valuePropName="checked"
              initialValue={false}
            >
              <Checkbox disabled={disabled}>Букво-численный счетчик</Checkbox>
            </Form.Item>
          </div>
        )}
      </div>
    );
  });
